import {
  FILTER_FUNCTION,
  FilterDataType,
  type NumberFilterArgument,
} from 'common/components/FilterBar/SoqlFilter';
import { type Filter } from 'common/components/FilterBar/types';
import { commaify } from 'common/formatNumber';

const MISSING_VALUES_INCLUDED_TEXT = ' (missing or null values included)';
const EXCLUDES_MISSING_VALUES_TEXT = 'Excludes missing values';

// this file is bundled into the PDF web worker, which has no `window`, so it must not
// touch window-dependent formatters (DataTypeFormatter). when the main thread has
// pre-formatted a value (currency/percent/decimals, WYSIWYG with the filter bar) we
// render that; otherwise fall back to window-free thousands separators.
const formatNumberValue = (value: number | string, formattedValue?: string): string => {
  if (formattedValue) {
    return formattedValue;
  }
  return commaify(value);
};

const NUMBER_FILTER_OPERATORS = {
  [FILTER_FUNCTION.EQUALS]: 'equals',
  [FILTER_FUNCTION.NOT_EQUAL]: 'not equal to',
  [FILTER_FUNCTION.LESS_THAN]: 'below',
  [FILTER_FUNCTION.LESS_THAN_EQUAL_TO]: 'at most',
  [FILTER_FUNCTION.GREATER_THAN]: 'above',
  [FILTER_FUNCTION.GREATER_THAN_EQUAL_TO]: 'at least',
} as const;

/**
 * Helper function to check if a filter is a number filter
 */
export function isNumberFilter(filter: Filter): boolean {
  return filter.dataTypeName === FilterDataType.NUMBER;
}

/**
 * Splits a number filter into column name and predicate parts for styled rendering
 * Similar to filterToHumanReadableParts for boolean filters
 * Returns { columnName: 'Amount', predicate: 'above 100' }
 */
export function numberFilterToHumanReadableParts(
  filter: Filter,
): { columnName: string; predicate: string } | null {
  if (!isNumberFilter(filter)) {
    return null;
  }

  const columnName = filter.columns[0]?.displayName || filter.columns[0]?.fieldName;
  if (!columnName) {
    return null;
  }

  const predicate = getNumberFilterPredicate(filter);
  if (!predicate) {
    return null;
  }

  return {
    columnName,
    predicate,
  };
}

/**
 * Gets just the predicate part of a number filter (without column name)
 * Returns strings like 'above 100', '1 to 100 (missing or null values included)', etc.
 */
function getNumberFilterPredicate(filter: Filter): string | null {
  switch (filter.function) {
    case FILTER_FUNCTION.EQUALS:
    case FILTER_FUNCTION.NOT_EQUAL:
    case FILTER_FUNCTION.LESS_THAN:
    case FILTER_FUNCTION.LESS_THAN_EQUAL_TO:
    case FILTER_FUNCTION.GREATER_THAN:
    case FILTER_FUNCTION.GREATER_THAN_EQUAL_TO:
      return getNumberComparisonPredicate(filter);

    case FILTER_FUNCTION.RANGE_INCLUSIVE:
    case FILTER_FUNCTION.RANGE_EXCLUSIVE:
    case FILTER_FUNCTION.VALUE_RANGE:
      return getNumberRangePredicate(filter);

    case FILTER_FUNCTION.EXCLUDE_NULL:
      return EXCLUDES_MISSING_VALUES_TEXT;

    default:
      return null;
  }
}

/**
 * Gets the predicate for number comparison filters (without column name)
 */
function getNumberComparisonPredicate(filter: Filter): string | null {
  const args = filter.arguments as NumberFilterArgument;
  if (!args || typeof args !== 'object' || Array.isArray(args)) {
    return null;
  }

  const includeNullValues = args.includeNullValues ?? false;
  const value = args.value;
  const operator = filter.function;

  if (!value) {
    return null;
  }

  // Get the operator text
  const operatorText = NUMBER_FILTER_OPERATORS[operator as keyof typeof NUMBER_FILTER_OPERATORS];
  if (!operatorText) {
    return null;
  }

  let result = `${operatorText} ${formatNumberValue(value, args.formattedValue)}`;

  // Handle special case for equals where it doesn't support missing values
  if (operator === FILTER_FUNCTION.EQUALS) {
    return result;
  }

  // Add missing values text if included
  if (includeNullValues) {
    result += MISSING_VALUES_INCLUDED_TEXT;
  }

  return result;
}

/**
 * Gets the predicate for number range filters (without column name)
 */
function getNumberRangePredicate(filter: Filter): string | null {
  const args = filter.arguments as NumberFilterArgument;
  if (!args || typeof args !== 'object' || Array.isArray(args)) {
    return null;
  }

  const includeNullValues = args.includeNullValues ?? false;
  const start = args.start;
  const end = args.end;
  const isInclusive = filter.function === FILTER_FUNCTION.RANGE_INCLUSIVE;

  if (start == null || end == null) {
    return null;
  }

  const formattedStart = formatNumberValue(start, args.formattedStart);
  const formattedEnd = formatNumberValue(end, args.formattedEnd);
  let result;
  if (isInclusive) {
    result = `${formattedStart} through ${formattedEnd}`;
  } else {
    result = `${formattedStart} to ${formattedEnd}`;
  }

  // Add missing values text if included
  if (includeNullValues) {
    result += MISSING_VALUES_INCLUDED_TEXT;
  }

  return result;
}
